import React, { useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import KpiSummaryTableHeader from './KpiSummaryTableHeader';
import { BaseTableRQ } from 'components/common/models/BaseClass';
import { search } from 'services/salesperson-kpi/kpiSummaryService';
import { search as searchTarget } from 'services/salesperson-kpi/kpiTargetService';
import PageHeader from 'components/common/PageHeader';
import KpiSummaryTable from './KpiSummaryTable';
import { usePaginationBe } from 'hooks/useBeHandle';
import PropTypes from 'prop-types';
import KpiSummaryFilter from './KpiSummaryFilter';
import { joinDataMultiDropdown } from 'helpers/dropdownFuncHelper';

const KpiSummaryDashboard = ({ isFromManagerPage = false }) => {
  const defaultRQ = { ...new BaseTableRQ(1, 10), isFromManagerPage };
  const [total, setTotal] = useState(0);
  const [previousSaleId, setPreviousSaleId] = useState(0);
  const [paging, setPaging] = useState({ ...defaultRQ });

  const [filter, setFilter] = useState({});
  const [isFilter, setIsFilter] = useState(false);

  const [allTargets, setAllTargets] = useState([]);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);
  const [forceReload, setForceReload] = useState(false);

  const [kpiSummaries, setKpiSummaries] = useState([]);

  // =====================================================================
  // LOAD TARGET
  // =====================================================================
  const loadAllTargets = async param => {
    setIsLoadingTargets(true);
    const rq = mapRQData(filter);
    try {
      const res = await searchTarget(param);
      const sorted = res?.items
        ? [...res.items].sort(
            (a, b) => new Date(a.applyFrom) - new Date(b.applyFrom)
          )
        : [];
      setAllTargets(sorted);
    } catch (e) {
      console.error(e);
      setAllTargets([]);
    }
    setPaging({ ...paging, ...rq, ...defaultRQ });
    setIsLoadingTargets(false);
  };

  // User page → load targets ngay từ đầu
  useEffect(() => {
    if (!isFromManagerPage) {
      loadAllTargets({ isGetAll: true, isFromManagerPage });
    }
  }, []);

  // =====================================================================
  // MAP SUMMARY + TARGET
  // =====================================================================
  const mapSummaryWithTarget = (rsSummary, targets) => {
    const items = rsSummary.items || [];

    // Manager page chưa chọn saleId → không map target
    if (isFromManagerPage && !filter?.saleId) {
      return items;
    }

    // Nếu không có target nào → group "No Target"
    if (!targets?.length) {
      return [
        { isTargetRow: true, label: 'No Target' },
        ...items.map(x => ({ ...x, target: null }))
      ];
    }

    const getMondayOfIsoWeek = (year, week) => {
      const simple = new Date(year, 0, 4);
      const dayOfWeek = simple.getDay() || 7;
      simple.setDate(simple.getDate() - dayOfWeek + 1 + (week - 1) * 7);
      simple.setHours(0, 0, 0, 0);
      return simple;
    };

    const result = [];
    const matched = new Set();

    targets.forEach((t, i) => {
      const from = new Date(t.applyFrom);
      const next =
        i < targets.length - 1 ? new Date(targets[i + 1].applyFrom) : null;

      const rangeSummaries = items.filter(s => {
        const monday = getMondayOfIsoWeek(s.year, s.weekNumber);
        return next ? monday >= from && monday < next : monday >= from;
      });

      rangeSummaries.forEach(s => matched.add(`${s.year}-${s.weekNumber}`));

      if (rangeSummaries.length > 0) {
        result.push({
          isTargetRow: true,
          label: `Target From: ${from.toLocaleDateString('en-GB')}`,
          target: t
        });

        rangeSummaries.forEach(s => result.push({ ...s, target: t }));
      }
    });

    const noTarget = items.filter(
      s => !matched.has(`${s.year}-${s.weekNumber}`)
    );

    if (noTarget.length > 0) {
      result.push({ isTargetRow: true, label: 'No Target', target: null });
      noTarget.forEach(s => result.push({ ...s, target: null }));
    }

    return result;
  };

  // =====================================================================
  // LOAD SUMMARY
  // =====================================================================
  const loadKpiSummarys = rq => {
    search(rq)
      .then(res => {
        if (!res) return;

        const mapped = mapSummaryWithTarget(res, allTargets);
        setKpiSummaries(mapped);
        setTotal(res.total);
      })
      .catch(console.error);

    setPaging(rq);
  };

  useEffect(() => {
    if (!isLoadingTargets) {
      const rq = mapRQData({ ...paging, ...filter });

      loadKpiSummarys(rq);
    }
  }, [isLoadingTargets, forceReload]);

  // =====================================================================
  // FILTER + PAGINATION
  // =====================================================================
  const { gotoPage, nextPage, previousPage } = usePaginationBe(
    paging,
    total,
    loadKpiSummarys
  );

  const toggelFilter = () => setIsFilter(!isFilter);

  const handleChange = (field, value) => {
    console.log(field, value);
    setFilter({ ...filter, [field]: value, pageNum: 1 });
  };

  const mapRQData = data => {
    if (!data) return null;

    const rq = { ...data };
    if (data.date instanceof Date) {
      rq.kpiRecordsDate = data.date.toISOString();
    } else {
      rq.kpiRecordsDate = undefined;
    }
    rq.saleIds = joinDataMultiDropdown(data?.sale);
    return rq;
  };

  const handleSubmit = async () => {
    const currentSaleId = filter?.saleId ?? 0;
    if (isFromManagerPage && filter?.saleId > 0) {
      if (currentSaleId > 0 && currentSaleId != previousSaleId) {
        await loadAllTargets({
          isGetAll: true,
          saleId: currentSaleId,
          isFromManagerPage
        });
        setPreviousSaleId(currentSaleId);
      } else {
        setForceReload(!forceReload);
      }
    } else {
      const rq = mapRQData(filter);
      loadKpiSummarys({ ...defaultRQ, ...rq });
    }
  };

  // =====================================================================
  // RENDER UI
  // =====================================================================
  return (
    <>
      <PageHeader
        title="Kpi Summary"
        description="Manage and view all available Kpi Summary from this page."
        className="mb-3"
      />

      <Row className="g-3 mb-3">
        <Col lg={12}>
          <Card>
            <Row style={{ padding: '10px' }}>
              <KpiSummaryTableHeader toggelFilter={toggelFilter} />

              {isFilter && (
                <Col
                  lg={12}
                  style={{ padding: 12, maxHeight: 250, overflowY: 'auto' }}
                >
                  <KpiSummaryFilter
                    displayOption={{
                      showHeader: true,
                      showFooter: true,
                      col: '6'
                    }}
                    handleChange={handleChange}
                    formData={filter}
                    handleSubmit={handleSubmit}
                    isFromManagerPage={isFromManagerPage}
                  />
                </Col>
              )}

              <KpiSummaryTable
                data={kpiSummaries}
                paging={paging}
                total={total}
                nextPage={nextPage}
                previousPage={previousPage}
                gotoPage={gotoPage}
                isFromManagerPage={isFromManagerPage}
                handleClickRow={(e, row) => console.log(row)}
              />
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

KpiSummaryDashboard.propTypes = { isFromManagerPage: PropTypes.bool };

export default KpiSummaryDashboard;
